import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallHook {
  isInstallable: boolean;
  isInstalled: boolean;
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  browserSupportsInstall: boolean;
  promptInstall: () => Promise<boolean>;
  dismissPrompt: () => void;
  showPromptBanner: boolean;
}

const DISMISSED_KEY = 'Tinybots.pwa.installPromptDismissed';
const DISMISSED_COUNT_KEY = 'Tinybots.pwa.dismissCount';
const MAX_DISMISSALS = 2;

export const usePWAInstall = (): PWAInstallHook => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [browserSupportsInstall, setBrowserSupportsInstall] = useState(false);
  const [showPromptBanner, setShowPromptBanner] = useState(false);

  useEffect(() => {
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (window.navigator as any).standalone === true;
      setIsInstalled(isStandalone || isIOSStandalone);
    };

    const checkDeviceAndBrowser = () => {
      const userAgent = window.navigator.userAgent || window.navigator.vendor || (window as any).opera;
      const regexp1 = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|tizen|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;
      const regexp2 = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i;
      const isMobileDevice = regexp1.test(userAgent) || regexp2.test(userAgent.substr(0, 4));
      setIsMobile(isMobileDevice);
      
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      setIsIOS(isIOSDevice);
      
      const isAndroidDevice = /Android/.test(userAgent);
      setIsAndroid(isAndroidDevice);
      
      // Detect browsers that support PWA installation
      // Chrome, Edge, Samsung Internet, Opera, Firefox Android support beforeinstallprompt
      // Safari iOS supports manual installation
      const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
      const isEdge = /Edg/.test(userAgent);
      const isSamsungBrowser = /SamsungBrowser/.test(userAgent);
      const isOpera = /OPR/.test(userAgent) || /Opera/.test(userAgent);
      const isFirefoxAndroid = /Firefox/.test(userAgent) && isAndroidDevice;
      
      // Browser supports PWA if it's one of the supported browsers
      const supportsInstall = isIOSDevice || isChrome || isEdge || isSamsungBrowser || isOpera || isFirefoxAndroid;
      setBrowserSupportsInstall(supportsInstall);
    };

    checkIfInstalled();
    checkDeviceAndBrowser();
    
    const shouldShowBanner = () => {
      const dismissedTimestamp = localStorage.getItem(DISMISSED_KEY);
      const dismissCount = parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '0', 10);
      
      if (dismissCount >= MAX_DISMISSALS) {
        return false;
      }
      
      if (dismissedTimestamp) {
        const dismissedDate = new Date(dismissedTimestamp);
        const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceDismissed < 7) {
          return false;
        }
      }
      
      return true;
    };

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      const dismissedTimestamp = localStorage.getItem(DISMISSED_KEY);
      const dismissCount = parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '0', 10);
      
      if (dismissCount >= MAX_DISMISSALS) {
        return;
      }
      
      if (dismissedTimestamp) {
        const dismissedDate = new Date(dismissedTimestamp);
        const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceDismissed < 7) {
          return;
        }
      }
      
      setTimeout(() => {
        setShowPromptBanner(true);
      }, 3000);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowPromptBanner(false);
      localStorage.removeItem(DISMISSED_KEY);
      localStorage.removeItem(DISMISSED_COUNT_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Fallback: If browser supports PWA but beforeinstallprompt hasn't fired after 5 seconds
    // (e.g., Firefox Android, iOS), show banner with manual instructions
    const fallbackTimer = setTimeout(() => {
      const userAgent = window.navigator.userAgent;
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      const isAndroidDevice = /Android/.test(userAgent);
      const isMobileDevice = isIOSDevice || isAndroidDevice;
      
      // Show banner if:
      // 1. It's a mobile device that supports PWA
      // 2. App is not already installed
      // 3. beforeinstallprompt hasn't fired (no deferredPrompt)
      // 4. User hasn't dismissed too many times
      if (isMobileDevice && !isInstalled && !deferredPrompt && shouldShowBanner()) {
        setShowPromptBanner(true);
      }
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowPromptBanner(false);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error prompting install:', error);
      return false;
    }
  };

  const dismissPrompt = () => {
    setShowPromptBanner(false);
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    
    const currentCount = parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '0', 10);
    localStorage.setItem(DISMISSED_COUNT_KEY, String(currentCount + 1));
  };

  return {
    isInstallable: !!deferredPrompt,
    isInstalled,
    isMobile,
    isIOS,
    isAndroid,
    browserSupportsInstall,
    promptInstall,
    dismissPrompt,
    showPromptBanner
  };
};
